/**
 * The Overall Status board's per-user settings: cards per row and which card
 * elements show. Stored server-side as one `UserPreference` under
 * `BOARD_PREFS_KEY`, so they follow the person between machines.
 */
export const BOARD_PREFS_KEY = 'board.overall-status'

export const MIN_BOARD_COLUMNS = 1
export const MAX_BOARD_COLUMNS = 8

export type BoardElement = 'productHistory' | 'inventory' | 'equipment' | 'capa' | 'auditTrail'

export const BOARD_ELEMENTS: { key: BoardElement; label: string }[] = [
  { key: 'productHistory', label: 'P / H' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'equipment', label: 'Equipment and Reserve' },
  { key: 'capa', label: 'CAPA' },
  { key: 'auditTrail', label: 'Audit Trail' },
]

export interface BoardPrefs {
  columns: number
  show: Record<BoardElement, boolean>
}

export const DEFAULT_BOARD_PREFS: BoardPrefs = {
  columns: 5,
  show: { productHistory: true, inventory: true, equipment: true, capa: true, auditTrail: true },
}

/**
 * Whatever is stored, made into valid prefs.
 *
 * A stored value is untrusted: an older shape, a hand-edited row, or a value
 * from a later version must still render a working board. Unknown keys are
 * dropped and missing ones take their default.
 */
export function coerceBoardPrefs(raw: unknown): BoardPrefs {
  const obj = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {}
  const columns =
    typeof obj.columns === 'number' && Number.isInteger(obj.columns)
      ? Math.min(MAX_BOARD_COLUMNS, Math.max(MIN_BOARD_COLUMNS, obj.columns))
      : DEFAULT_BOARD_PREFS.columns
  const rawShow = typeof obj.show === 'object' && obj.show !== null ? (obj.show as Record<string, unknown>) : {}
  const show = { ...DEFAULT_BOARD_PREFS.show }
  for (const { key } of BOARD_ELEMENTS) {
    if (typeof rawShow[key] === 'boolean') show[key] = rawShow[key] as boolean
  }
  return { columns, show }
}

export function isDefaultBoardPrefs(prefs: BoardPrefs): boolean {
  return JSON.stringify(coerceBoardPrefs(prefs)) === JSON.stringify(DEFAULT_BOARD_PREFS)
}
